import 'core-js/stable'
import 'regenerator-runtime/runtime'
import 'url-search-params-polyfill'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import asyncActions from 'redux-thunk'
import { createStore, applyMiddleware, compose } from 'redux'
import {
	setOrigin,
	setContext,
	setConfiguration,
	POST,
} from '@wellsfargo/ecpfs-authority'
import appReducer from './reducers'
import { getComponentDetails } from './actions/login'
import { appInit } from './appInit'
import App from './AppContainer'
import { ThemeProvider } from '@wf-wfria/pioneer-core'
import LocalizedMessagesProvider from './containers/LocalizedMessagesProvider'
import { FORDPRO, PLINFOLEASE, RICOH } from './businessSegmentBrandIds'
import fordProFavicon from './fordpro_favicon.png'
import ricohLogo from './Ricoh_Logo.png'
import privateLabelLogo from './private_label_logo.png'
import packageJson from '../package.json'

const { name, version, config } = packageJson
;(function versions(name, version, window) {
	const versions = (window.versions = window.versions || {})
	/* istanbul ignore next */
	const values = new Set(versions[name])
	values.add(version)
	versions[name] = Array.from(values)
})(name, version, window)

const {
	senderApplicationId,
	senderHostName,
	senderMessageId,
	csrfHeaderId,
	csrfTokenId,
	apiContext,
} = config

setOrigin(document.location.origin)
setContext(apiContext)
setConfiguration({
	senderApplicationId,
	senderHostName,
	senderMessageId,
	csrfHeaderId,
	csrfTokenId,
	'X-Requested-With': 'XMLHttpRequest',
})

const composeEnhancers = (middleware) => {
	// comment out before PR
	//return (window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose)(middleware)
	return compose(middleware)
}

const container = document.getElementById('root')

const store = createStore(
	appReducer,
	composeEnhancers(applyMiddleware(asyncActions)),
)

export function initialize(getComponentDetailsError) {
	const componentDetailsError = getComponentDetailsError.error
		? getComponentDetailsError.error
		: null
	appInit(store, renderDom, componentDetailsError)
}

export function getFaviconHrefByMode(mode) {
	switch (mode) {
		case FORDPRO:
			return fordProFavicon
		case RICOH:
			return ricohLogo
		case PLINFOLEASE:
			return privateLabelLogo
		default:
			return `${process.env.PUBLIC_URL}/favicon.ico`
	}
}

export function setFaviconByMode(mode) {
	if (!document || !document.head) {
		return
	}

	let favicon = document.querySelector("link[rel='icon']")
	if (!favicon) {
		favicon = document.createElement('link')
		favicon.setAttribute('rel', 'icon')
		document.head.appendChild(favicon)
	}

	favicon.setAttribute('href', getFaviconHrefByMode(mode))
}

export async function refreshCsrf() {
	const timeoutInterval = 15 * 60 * 1000
	try {
		await callSignOutPng()
	} catch (error) {
		// console.error('Error in callSignOutPng:', error)
	}
	try {
		await refreshCSRFv2()
		store
			.dispatch(getComponentDetails())
			.then((response) => {
				initialize(response)
			})
			.catch((error) => {
				initialize(error)
			})
	} catch (error) {
		container.render(
			<p>
				<h1 style={{ color: 'coral', paddingTop: '100px', paddingLeft: '35%' }}>
					Error! Please try again
				</h1>
			</p>,
		)
	}

	setTimeout(refreshCsrf, timeoutInterval)
}

export function setCsrfTokenAndCsrfHeader(csrfToken, csrfHeader) {
	const token = document.getElementById('__csrftoken')
	if (token instanceof HTMLMetaElement) {
		token.setAttribute('content', csrfToken)
	}
	const header = document.getElementById('__csrfheader')
	if (header instanceof HTMLMetaElement) {
		header.setAttribute('content', csrfHeader)
	}
}

export async function refreshCSRFv2() {
	const response = await POST(`${apiContext}login/csrfToken.json`)
	const { csrfToken = '', csrfHeader = '' } = response || {}
	setCsrfTokenAndCsrfHeader(csrfToken, csrfHeader)
}
export async function fetchCsrf() {
	try {
		updateCsrfToken()
		store
			.dispatch(getComponentDetails())
			.then((response) => {
				initialize(response)
			})
			.catch((error) => {
				initialize(error)
			})
	} catch (error) {
		console.error('error', error)
	}
}

export async function callSignOutPng() {
	const response = await POST(`${apiContext}pa/oidc/logout.png`)
	const signOutResponseData = await response.blob()
	console.log('signOutResponseData==> ' + signOutResponseData)
}

if (typeof window !== 'undefined' && document.getElementById('root')) {
	refreshCsrf()
}

export function renderDom() {
	const root = createRoot(container)
	const mode = store.getState().loginWidget.mode

	// Set data-brand-id on the root container and body for modal panels
	if (container) {
		container.setAttribute('data-brand-id', mode || '')
	}
	if (document.body) {
		document.body.setAttribute('data-brand-id', mode || '')
	}
	setFaviconByMode(mode)

	root.render(
		<ThemeProvider baseTheme="vantage">
			<Provider store={store}>
				<LocalizedMessagesProvider>
					<App store={store} />
				</LocalizedMessagesProvider>
			</Provider>
		</ThemeProvider>,
	)

	// Subscribe to store changes to update data-brand-id when mode changes
	store.subscribe(() => {
		const newMode = store.getState().loginWidget.mode
		if (container) {
			container.setAttribute('data-brand-id', newMode || '')
		}
		if (document.body) {
			document.body.setAttribute('data-brand-id', newMode || '')
		}
		setFaviconByMode(newMode)
	})
}

export async function updateCsrfToken() {
	try {
		const options = {
			method: 'POST',
			headers: {
				'FETCH-CSRF-TOKEN': '1',
				'X-Requested-With': 'XMLHttpRequest',
			},
		}
		const response = await fetch(
			`${apiContext}CSRFGuardJavaScriptServlet.js`,
			options,
		)

		const data = await response.text()
		const token_pair = data.split(':')
		const token_name = token_pair[0]
		const token_value = token_pair[1]

		setCsrfTokenAndCsrfHeader(token_value, token_name)
	} catch (error) {
		error(error)
	}
}
